/**
 * 紧凑项目卡片组件
 * 用于在Dashboard中以更高密度显示项目信息
 * 背景色根据预算使用率渐变着色
 */
import React from 'react';
import { Card, Progress, Tag, Typography, Space, Tooltip } from 'antd';
import {
  SyncOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  PauseCircleOutlined
} from '@ant-design/icons';
import type { ProjectOverviewData } from '../../services/dashboardService';
import { DashboardService } from '../../services/dashboardService';
import { getBudgetBasedColors } from '../../utils/colorUtils';

const { Text } = Typography;

export interface CompactProjectCardProps {
  project: ProjectOverviewData;
  onClick?: (project: ProjectOverviewData) => void;
  isMobile?: boolean;
}

// 项目状态配置
const STATUS_CONFIG: Record<string, { color: string; icon: React.ReactNode }> = {
  '进行中': { color: 'processing', icon: <SyncOutlined spin /> },
  '已完成': { color: 'success', icon: <CheckCircleOutlined /> },
  '待开始': { color: 'default', icon: <ExclamationCircleOutlined /> },
  '已暂停': { color: 'warning', icon: <PauseCircleOutlined /> }
};

export const CompactProjectCard: React.FC<CompactProjectCardProps> = ({
  project,
  onClick,
  isMobile = false
}) => {
  const health = DashboardService.calculateProjectHealth(project.budget, project.actualCost);
  const statusConfig = STATUS_CONFIG[project.status] || STATUS_CONFIG['待开始'];
  
  // 获取基于预算使用率的颜色
  const budgetColors = getBudgetBasedColors(project.budget, project.actualCost);

  // 格式化金额显示
  const formatMoney = (value: number): string => {
    if (value >= 10000) {
      return `¥${(value / 10000).toFixed(1)}万`;
    }
    return `¥${value.toLocaleString()}`;
  };

  // 生成预算提示文本
  const getBudgetTooltip = () => {
    if (budgetColors.status === 'unset') {
      return '未设置预算';
    }
    return `预算使用: ${budgetColors.percentage.toFixed(1)}% (${budgetColors.statusText})`;
  };

  return (
    <Tooltip title={getBudgetTooltip()} placement="top">
      <Card
        hoverable
        size="small"
        onClick={() => onClick?.(project)}
        style={{ 
          cursor: onClick ? 'pointer' : 'default',
          height: '100%',
          backgroundColor: budgetColors.backgroundColor,
          borderColor: budgetColors.borderColor,
          transition: 'all 0.3s ease'
        }}
        bodyStyle={{ 
          padding: isMobile ? '8px 12px' : '12px 16px'
        }}
      >
        {/* 标题行：项目名称 + 状态 */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: 8
        }}>
          <Tooltip title={project.name}>
            <Text 
              strong 
              style={{ 
                fontSize: isMobile ? 13 : 14,
                maxWidth: isMobile ? 120 : 150,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {project.name}
            </Text>
          </Tooltip>
          <Tag 
            color={statusConfig.color} 
            icon={statusConfig.icon}
            style={{ marginRight: 0, fontSize: 11 }}
          >
            {project.status}
          </Tag>
        </div>

        {/* 预算使用比例 */}
        <Tooltip title={`预算使用: ${budgetColors.percentage.toFixed(1)}% (${formatMoney(project.actualCost)} / ${formatMoney(project.budget)})`}>
          <Progress
            percent={budgetColors.percentage}
            size="small"
            strokeColor={budgetColors.percentage >= 100 ? '#ff4d4f' : budgetColors.percentage >= 80 ? '#faad14' : '#52c41a'}
            style={{ marginBottom: 6 }}
            format={(percent) => `${percent?.toFixed(0)}%`}
          />
        </Tooltip>

        {/* 预算信息 */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Tooltip title={`实际成本: ${formatMoney(project.actualCost)} / 预算: ${formatMoney(project.budget)}`}>
            <Space size={4}>
              <Text 
                style={{ 
                  fontSize: 12, 
                  color: health.color 
                }}
              >
                {formatMoney(project.actualCost)}
              </Text>
              <Text type="secondary" style={{ fontSize: 11 }}>
                / {formatMoney(project.budget)}
              </Text>
            </Space>
          </Tooltip>
          {project.budget > 0 && (
            <Text 
              style={{ 
                fontSize: 11, 
                color: health.color,
                fontWeight: 500
              }}
            >
              {health.percentage.toFixed(0)}%
            </Text>
          )}
        </div>
      </Card>
    </Tooltip>
  );
};

export default CompactProjectCard;
